import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    minWidth: '400px',
    padding: theme.spacing(2)
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    textAlign: 'center'
  },
  spinner: {
    marginBottom: theme.spacing(3)
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 500
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3)
  },
  progressContainer: {
    width: '100%',
    marginTop: theme.spacing(2)
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1)
  },
  progressBar: {
    height: 8,
    borderRadius: 4
  }
}));

const LoadingDialog = ({ open, progress }) => {
  const classes = useStyles();

  const totalProgress = useMemo(() => {
    if (!progress || !progress.total || progress.total === 0) {
      return 0;
    }
    const { geocoding = 0, entityFields = 0, total } = progress;
    return Math.round(((geocoding + entityFields) / total) * 100);
  }, [progress]);

  const progressDetails = useMemo(() => {
    if (!progress) {
      return null;
    }
    const { geocoding = 0, entityFields = 0 } = progress;
    const details = [];
    if (geocoding > 0) {
      details.push(`Locations geocoded: ${geocoding}`);
    }
    if (entityFields > 0) {
      details.push(`Records loaded: ${entityFields}`);
    }
    return details.length > 0 ? details.join(' • ') : null;
  }, [progress]);

  return (
    <Dialog
      open={open}
      disableBackdropClick
      disableEscapeKeyDown
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogContent className={classes.content}>
        <CircularProgress size={56} className={classes.spinner} />
        <Typography variant="h6" className={classes.title}>
          Preparing your records...
        </Typography>
        <Typography variant="body2" className={classes.subtitle}>
          Loading location data and record information from the server.
          <br />
          Please wait while we prepare your batch for editing.
        </Typography>
        {progress && progress.total > 0 && (
          <div className={classes.progressContainer}>
            <div className={classes.progressLabel}>
              <Typography variant="caption" color="textSecondary">
                {progressDetails || 'Processing...'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {totalProgress}%
              </Typography>
            </div>
            <LinearProgress
              variant="determinate"
              value={totalProgress}
              className={classes.progressBar}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

LoadingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  progress: PropTypes.shape({
    geocoding: PropTypes.number,
    entityFields: PropTypes.number,
    total: PropTypes.number
  })
};

export default memo(LoadingDialog);
